import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Calendar, LogOut, User, Plus, Crown, Settings, Users, Download, Menu, X, MessageCircle, Briefcase, Building, Globe, ChevronDown, Home, FileText, ShoppingBag, Heart, BookOpen, UserCircle, HelpCircle, Bell, BarChart3 } from 'lucide-react';
import AuthDialog from './AuthDialog';
import TutorialTour from './TutorialTour';
import CommunitySelector from './CommunitySelector';
import { showLargeToast } from '@/components/ui/sonner';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

import { useIsIOSApp } from '@/hooks/useIsIOSApp';
import AppStoreBadges from '@/components/AppStoreBadges';

const Navbar = () => {
  const { user, logout } = useAuth();
  const isIOSApp = useIsIOSApp();
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [showTutorial, setShowTutorial] = useState(false);
  const [authMessage, setAuthMessage] = useState('');
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [pendingConnections, setPendingConnections] = useState(0);
  const [newRsvps, setNewRsvps] = useState(0);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [showAppBanner, setShowAppBanner] = useState(() => {
    return !sessionStorage.getItem('app-banner-dismissed');
  });

  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      fetchPendingConnections();
      fetchNewRsvps();
      fetchUnreadNotifications();
      
      // Delayed re-fetch to catch notifications marked read by service worker just before app opened
      setTimeout(() => {
        fetchUnreadCount();
        fetchUnreadNotifications();
      }, 1500);
      
      // Poll for updates every 30 seconds
      const interval = setInterval(() => {
        fetchUnreadCount();
        fetchPendingConnections();
        fetchNewRsvps();
        fetchUnreadNotifications();
      }, 30000);

      // Refresh counts when a push notification is tapped
      const handleSWMessage = async (event) => {
        if (event.data?.type === 'NOTIFICATION_CLICKED') {
          const url = event.data.url;
          // Navigate via React Router (instant, no full page reload)
          if (url && url !== window.location.pathname) {
            navigate(url);
          }
          // Mark all notifications as read to clear the badge
          try {
            await axios.post(`${API}/notifications/read-all`);
          } catch {}
          // Refresh badge counts immediately + delayed
          fetchUnreadCount();
          fetchUnreadNotifications();
          fetchPendingConnections();
          setTimeout(() => {
            fetchUnreadCount();
            fetchUnreadNotifications();
            fetchPendingConnections();
          }, 1000);
          setTimeout(() => {
            fetchUnreadCount();
            fetchUnreadNotifications();
            fetchPendingConnections();
          }, 3000);
        }
      };
      navigator.serviceWorker?.addEventListener('message', handleSWMessage);

      // Refresh counts when app comes back to foreground
      const handleVisibility = () => {
        if (document.visibilityState === 'visible') {
          fetchUnreadCount();
          fetchUnreadNotifications();
          fetchPendingConnections();
        }
      };
      document.addEventListener('visibilitychange', handleVisibility);

      // Refresh counts when notifications are marked as read
      const handleNotificationsUpdated = () => {
        fetchUnreadCount();
        fetchUnreadNotifications();
        fetchPendingConnections();
      };
      window.addEventListener('notifications-updated', handleNotificationsUpdated);

      return () => {
        clearInterval(interval);
        navigator.serviceWorker?.removeEventListener('message', handleSWMessage);
        document.removeEventListener('visibilitychange', handleVisibility);
        window.removeEventListener('notifications-updated', handleNotificationsUpdated);
      };
    }
  }, [user]);

  const fetchUnreadCount = async () => {
    try {
      const response = await axios.get(`${API}/messages/unread-count`);
      setUnreadMessages(response.data.unread_count);
    } catch (error) {
      // Silent fail
    }
  };

  const fetchPendingConnections = async () => {
    try {
      const response = await axios.get(`${API}/connections`);
      setPendingConnections(response.data.pending_requests?.length || 0);
    } catch (error) {
      // Silent fail
    }
  };

  const fetchNewRsvps = async () => {
    try {
      const response = await axios.get(`${API}/events/my-events/rsvp-count`);
      setNewRsvps(response.data.total_new_rsvps || 0);
    } catch (error) {
      // Silent fail
    }
  };

  const fetchUnreadNotifications = async () => {
    try {
      const response = await axios.get(`${API}/notifications/unread-count`);
      setUnreadNotifications(response.data.unread_count);
    } catch (error) {
      // Silent fail
    }
  };

  const [isIOS, setIsIOS] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches;
    const alreadyInstalled = localStorage.getItem('pwa-installed');
    setIsStandalone(standalone || alreadyInstalled === 'true');
    
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    
    // Listen for app installed event
    window.addEventListener('appinstalled', () => {
      localStorage.setItem('pwa-installed', 'true');
      setIsStandalone(true);
      // Toast is handled by Landing.js to avoid duplicates
    });
    
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    // Check if actually running as standalone PWA
    const actuallyStandalone = window.matchMedia('(display-mode: standalone)').matches;
    
    if (actuallyStandalone) {
      showLargeToast(
        'You\'re Using the App!',
        'You\'re already inside the ExpatsSphere app. Enjoy!',
        'success'
      );
      return;
    }
    
    // If localStorage says installed but we're in browser, user deleted app or is in browser
    const wasInstalled = localStorage.getItem('pwa-installed') === 'true';
    
    if (wasInstalled) {
      // App was installed before - show how to open it
      showLargeToast(
        'Open ExpatsSphere',
        'Look for the ExpatsSphere icon on your home screen and tap it to open the app. If you can\'t find it, try reinstalling.',
        'info'
      );
      return;
    }
    
    if (isIOS) {
      showLargeToast(
        'Install on iPhone/iPad',
        '1. Tap the Share button (□↑) at the bottom of your screen\n2. Scroll down and tap "Add to Home Screen"\n3. Tap "Add" to confirm',
        'info'
      );
      return;
    }
    
    if (deferredPrompt) {
      // Just show the browser prompt - success toast fires via 'appinstalled' event
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        localStorage.setItem('pwa-installed', 'true');
        setIsStandalone(true);
        // The 'appinstalled' event will show the completion toast
      }
      setDeferredPrompt(null);
    } else {
      // Android Chrome fallback - show instructions
      showLargeToast(
        'Install ExpatsSphere',
        'Tap the ⋮ menu (3 dots) in your browser\'s top right corner, then tap "Install App" or "Add to Home Screen" to install.',
        'info'
      );
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const handleProtectedNavigation = (path, action) => {
    if (user) {
      navigate(path);
    } else {
      setAuthMessage(`Become a member to ${action}`);
      setShowAuth(true);
    }
  };

  return (
    <>
      <nav
        className="fixed top-0 left-0 right-0 z-50 bg-background md:bg-background/80 md:backdrop-blur-xl border-b border-border/40"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-14 md:h-20">
            <Link to="/" className="flex items-center space-x-1.5 md:space-x-3 flex-shrink-0" data-testid="nav-logo">
              <div className="w-10 h-10 md:w-20 md:h-20 rounded-full flex items-center justify-center shadow-lg border-2 md:border-3 border-white overflow-hidden flex-shrink-0">
                <img 
                  src="https://static.prod-images.emergentagent.com/jobs/a4035128-ba5f-41e2-8c85-a2f1cef97a56/images/c96ad2beeee3b28854016505e6ee1acce69f2fb6e7cdac0f802648d487e720c3.png" 
                  alt="Diverse expats"
                  className="w-full h-full object-cover"
                />
              </div>
              <div className="flex flex-col items-center">
                <span className="text-lg md:text-3xl font-heading font-bold text-foreground leading-none mb-0.5 md:mb-1">
                  Expats<span className="italic text-primary">Sphere</span>
                </span>
                <span className="hidden sm:block text-xs md:text-base text-accent font-bold tracking-wide leading-none" style={{ fontFamily: "'Satisfy', cursive" }}>
                  Connecting Expats Globally
                </span>
              </div>
            </Link>

            {/* Mobile City Selector - between logo and icons (browser only, not standalone app) */}
            {user && !window.matchMedia('(display-mode: standalone)').matches && !window.navigator.standalone && (
              <div className="md:hidden flex-shrink-0 ml-2">
                <CommunitySelector user={user} variant="navbar" />
              </div>
            )}

            {/* Mobile Header Actions - Notifications, Messages & Menu */}
            <div className="md:hidden flex items-center gap-0 flex-shrink-0">
              {user && (
                <>
                  {/* Bell Icon for Notifications (pending connections + in-app notifications) */}
                  <Link to="/notifications" className="relative p-1" data-testid="mobile-notifications-bell">
                    <Bell className="w-5 h-5" />
                    {(pendingConnections + unreadNotifications) > 0 && (
                      <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center px-0.5">
                        {(pendingConnections + unreadNotifications) > 9 ? '9+' : (pendingConnections + unreadNotifications)}
                      </span>
                    )}
                  </Link>
                  {/* Messages Icon */}
                  <Link to="/messages" className="relative p-1" data-testid="mobile-messages-icon">
                    <MessageCircle className="w-5 h-5" />
                    {unreadMessages > 0 && (
                      <span className="absolute top-0.5 right-0.5 w-2 h-2 bg-red-500 rounded-full"></span>
                    )}
                  </Link>
                </>
              )}
              {!user && (
                <Button
                  onClick={() => { setAuthMessage(''); setShowAuth(true); }}
                  variant="ghost"
                  size="sm"
                  className="text-[11px] font-semibold px-1.5 h-7"
                >
                  Sign In
                </Button>
              )}
              {/* Mobile Menu Button - Always visible */}
              <button
                className="p-1 rounded-lg hover:bg-gray-100 flex-shrink-0"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-button"
                aria-label="Open menu"
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </button>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1 lg:space-x-3">
              {/* Explore Mega Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button 
                    className="bg-accent hover:bg-accent/90 text-black font-bold px-3 lg:px-6 h-10 lg:h-11 rounded-full border-2 border-accent shadow-md text-sm lg:text-base" 
                    data-testid="nav-explore"
                  >
                    Explore
                    <ChevronDown className="w-4 h-4 ml-1 lg:ml-2" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-80 p-4">
                  <div className="grid grid-cols-2 gap-2">
                    {/* Events Section with subcategories */}
                    <div className="col-span-2 space-y-1 mb-2">
                      <Link to="/events">
                        <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-accent/10 transition-colors cursor-pointer group">
                          <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center group-hover:bg-accent/20">
                            <Calendar className="w-5 h-5 text-accent" />
                          </div>
                          <div>
                            <p className="font-bold text-sm">Browse Events</p>
                            <p className="text-xs text-muted-foreground">Discover meetups & activities</p>
                          </div>
                        </div>
                      </Link>
                      <div className="flex gap-2 pl-4">
                        <Link to="/create-event" className="flex-1">
                          <div className="flex items-center gap-2 p-2 rounded-lg hover:bg-accent/10 transition-colors cursor-pointer">
                            <Plus className="w-4 h-4 text-accent" />
                            <span className="text-sm font-medium">Create Event</span>
                          </div>
                        </Link>
                        {user && (
                          <Link to="/dashboard" className="flex-1">
                            <div className="flex items-center gap-2 p-2 rounded-lg hover:bg-accent/10 transition-colors cursor-pointer">
                              <Calendar className="w-4 h-4 text-accent" />
                              <span className="text-sm font-medium">My Events</span>
                            </div>
                          </Link>
                        )}
                      </div>
                    </div>
                    
                    <Link to="/groups">
                      <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-primary/10 transition-colors cursor-pointer group">
                        <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center group-hover:bg-primary/20">
                          <Users className="w-5 h-5 text-primary" />
                        </div>
                        <div>
                          <p className="font-bold text-sm">Groups</p>
                          <p className="text-xs text-muted-foreground">Interest communities</p>
                        </div>
                      </div>
                    </Link>
                    <Link to="/polls" data-testid="nav-polls-link">
                      <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-violet-500/10 transition-colors cursor-pointer group">
                        <div className="w-10 h-10 bg-violet-500/10 rounded-lg flex items-center justify-center group-hover:bg-violet-500/20">
                          <BarChart3 className="w-5 h-5 text-violet-600" />
                        </div>
                        <div>
                          <p className="font-bold text-sm">Polls</p>
                          <p className="text-xs text-muted-foreground">Gauge interest before events</p>
                        </div>
                      </div>
                    </Link>
                    <Link to="/find-expats">
                      <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-green-500/10 transition-colors cursor-pointer group">
                        <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center group-hover:bg-green-500/20">
                          <Globe className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-bold text-sm">Find Expats</p>
                          <p className="text-xs text-muted-foreground">Connect worldwide</p>
                        </div>
                      </div>
                    </Link>
                    <Link to="/guides">
                      <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-blue-500/10 transition-colors cursor-pointer group">
                        <div className="w-10 h-10 bg-blue-500/10 rounded-lg flex items-center justify-center group-hover:bg-blue-500/20">
                          <MessageCircle className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-bold text-sm">Resources</p>
                          <p className="text-xs text-muted-foreground">Tips & articles</p>
                        </div>
                      </div>
                    </Link>
                    <Link to="/jobs">
                      <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-purple-500/10 transition-colors cursor-pointer group">
                        <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center group-hover:bg-purple-500/20">
                          <Briefcase className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-bold text-sm">Job Board</p>
                          <p className="text-xs text-muted-foreground">Career opportunities</p>
                        </div>
                      </div>
                    </Link>
                    <Link to="/housing">
                      <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-teal-500/10 transition-colors cursor-pointer group">
                        <div className="w-10 h-10 bg-teal-500/10 rounded-lg flex items-center justify-center group-hover:bg-teal-500/20">
                          <Home className="w-5 h-5 text-teal-600" />
                        </div>
                        <div>
                          <p className="font-bold text-sm">Housing</p>
                          <p className="text-xs text-muted-foreground">Rent & buy properties</p>
                        </div>
                      </div>
                    </Link>
                    <Link to="/marketplace">
                      <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-pink-500/10 transition-colors cursor-pointer group">
                        <div className="w-10 h-10 bg-pink-500/10 rounded-lg flex items-center justify-center group-hover:bg-pink-500/20">
                          <ShoppingBag className="w-5 h-5 text-pink-600" />
                        </div>
                        <div>
                          <p className="font-bold text-sm">Marketplace</p>
                          <p className="text-xs text-muted-foreground">Buy & sell items</p>
                        </div>
                      </div>
                    </Link>
                    <Link to="/businesses">
                      <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-amber-500/10 transition-colors cursor-pointer group">
                        <div className="w-10 h-10 bg-amber-500/10 rounded-lg flex items-center justify-center group-hover:bg-amber-500/20">
                          <Building className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-bold text-sm">Directory</p>
                          <p className="text-xs text-muted-foreground">Local services</p>
                        </div>
                      </div>
                    </Link>
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>

              {!isStandalone && (
                <Button 
                  onClick={handleInstallClick}
                  variant="outline"
                  className="font-bold px-3 lg:px-5 h-10 lg:h-11 rounded-full border-2 text-sm lg:text-base" 
                  data-testid="nav-install-app"
                >
                  <Download className="w-4 h-4 mr-1 lg:mr-2" />
                  <span className="hidden lg:inline">Install App</span>
                  <span className="lg:hidden">Install</span>
                </Button>
              )}

              {user ? (
                <>
                  {/* Community Selector */}
                  <CommunitySelector user={user} variant="navbar" />

                  {/* Bell Icon for Notifications (pending connections + in-app notifications) */}
                  <Link to="/notifications" data-testid="desktop-notifications-bell">
                    <Button
                      variant="ghost"
                      className="relative rounded-full w-10 h-10 p-0"
                    >
                      <Bell className="w-5 h-5" />
                      {(pendingConnections + unreadNotifications) > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                          {(pendingConnections + unreadNotifications) > 9 ? '9+' : (pendingConnections + unreadNotifications)}
                        </span>
                      )}
                    </Button>
                  </Link>

                  {/* Messages Icon */}
                  <Link to="/messages" data-testid="desktop-messages-icon">
                    <Button
                      variant="ghost"
                      className="relative rounded-full w-10 h-10 p-0"
                    >
                      <MessageCircle className="w-5 h-5" />
                      {unreadMessages > 0 && (
                        <span className="absolute -top-1 -right-1 bg-primary text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                          {unreadMessages > 9 ? '9+' : unreadMessages}
                        </span>
                      )}
                    </Button>
                  </Link>

                  {!user.is_member && !isIOSApp && (
                    <Link to="/membership">
                      <Button
                        variant="ghost"
                        className="font-medium text-primary hover:text-primary/90"
                        data-testid="nav-upgrade"
                      >
                        <Crown className="w-4 h-4 mr-2" />
                        Upgrade
                      </Button>
                    </Link>
                  )}

                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="ghost"
                        className="rounded-full w-10 h-10 p-0 relative"
                        data-testid="nav-user-menu"
                      >
                        <div className="w-10 h-10 bg-accent rounded-full flex items-center justify-center font-bold text-accent-foreground">
                          {user.name.charAt(0).toUpperCase()}
                        </div>
                        {newRsvps > 0 && (
                          <span className="absolute -top-1 -right-1 bg-green-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                            {newRsvps > 9 ? '9+' : newRsvps}
                          </span>
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-48">
                      <DropdownMenuItem onClick={() => navigate('/dashboard')} data-testid="nav-dashboard" className="relative">
                        <Calendar className="w-4 h-4 mr-2" />
                        My Events
                        {newRsvps > 0 && (
                          <span className="ml-auto bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                            {newRsvps} new RSVP{newRsvps > 1 ? 's' : ''}
                          </span>
                        )}
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/my-submissions')} data-testid="nav-my-submissions">
                        <FileText className="w-4 h-4 mr-2" />
                        My Submissions
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/saved')} data-testid="nav-saved-items">
                        <Heart className="w-4 h-4 mr-2" />
                        Saved Items
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="nav-profile">
                        <User className="w-4 h-4 mr-2" />
                        Profile
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/privacy-settings')} data-testid="nav-privacy">
                        <Settings className="w-4 h-4 mr-2" />
                        Privacy Settings
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => { localStorage.removeItem('tutorialCompleted'); setShowTutorial(true); }} data-testid="nav-tutorial">
                        <HelpCircle className="w-4 h-4 mr-2" />
                        Take a Tour
                      </DropdownMenuItem>
                      {!isStandalone && (
                        <DropdownMenuItem onClick={handleInstallClick} data-testid="nav-download-app">
                          <Download className="w-4 h-4 mr-2" />
                          Download App
                        </DropdownMenuItem>
                      )}
                      {isStandalone && (
                        <DropdownMenuItem disabled className="text-muted-foreground">
                          <Download className="w-4 h-4 mr-2" />
                          App Installed ✓
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={handleLogout} data-testid="nav-logout">
                        <LogOut className="w-4 h-4 mr-2" />
                        Logout
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <Button
                  onClick={() => {
                    setAuthMessage('');
                    setShowAuth(true);
                  }}
                  className="bg-primary text-primary-foreground hover:bg-primary/90 h-10 lg:h-12 px-4 lg:px-8 rounded-full font-bold brutal-shadow text-sm lg:text-base whitespace-nowrap"
                  data-testid="nav-login"
                >
                  Sign In
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Smart App Banner for logged-in web users */}
      {user && showAppBanner && !isStandalone && !isIOSApp && (
        <div className="fixed top-14 md:top-20 left-0 right-0 z-40 bg-primary/5 border-b border-primary/20 px-3 py-2" style={{ marginTop: 'env(safe-area-inset-top, 0px)' }}>
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <p className="text-xs font-medium text-foreground whitespace-nowrap">Get the app</p>
              <AppStoreBadges size="sm" />
            </div>
            <button
              onClick={() => { setShowAppBanner(false); sessionStorage.setItem('app-banner-dismissed', 'true'); }}
              className="text-muted-foreground hover:text-foreground p-1 flex-shrink-0"
              aria-label="Dismiss"
              data-testid="dismiss-app-banner"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Mobile Menu - Outside nav for proper positioning */}
      {mobileMenuOpen && (
        <div
          className="md:hidden fixed inset-x-0 bottom-0 bg-background z-40 overflow-y-auto"
          style={{ top: 'calc(3.5rem + env(safe-area-inset-top, 0px))' }}
        >
          <div className="py-4 px-4 pb-24">
            <div className="flex flex-col space-y-2">
              {/* Explore Section */}
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-2 pt-2">Explore</p>
              
              {/* Events with subcategories */}
              <div className="space-y-1">
                <Link to="/events" onClick={() => setMobileMenuOpen(false)}>
                  <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-accent/10">
                    <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <p className="font-bold">Browse Events</p>
                      <p className="text-xs text-muted-foreground">Discover meetups & activities</p>
                    </div>
                  </div>
                </Link>
                <Link to="/create-event" onClick={() => setMobileMenuOpen(false)}>
                  <div className="flex items-center gap-3 p-3 pl-8 rounded-xl hover:bg-accent/10">
                    <div className="w-8 h-8 bg-accent/20 rounded-lg flex items-center justify-center">
                      <Plus className="w-4 h-4 text-accent" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">Create Event</p>
                    </div>
                  </div>
                </Link>
                {user && (
                  <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                    <div className="flex items-center gap-3 p-3 pl-8 rounded-xl hover:bg-accent/10">
                      <div className="w-8 h-8 bg-accent/20 rounded-lg flex items-center justify-center">
                        <Calendar className="w-4 h-4 text-accent" />
                      </div>
                      <div>
                        <p className="font-medium text-sm">My Events</p>
                      </div>
                    </div>
                  </Link>
                )}
              </div>

              <Link to="/groups" onClick={() => setMobileMenuOpen(false)}>
                <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-primary/10">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-bold">Groups</p>
                    <p className="text-xs text-muted-foreground">Interest communities</p>
                  </div>
                </div>
              </Link>

              <Link to="/polls" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-polls-link">
                <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-violet-500/10">
                  <div className="w-10 h-10 bg-violet-500/10 rounded-lg flex items-center justify-center">
                    <BarChart3 className="w-5 h-5 text-violet-600" />
                  </div>
                  <div>
                    <p className="font-bold">Polls</p>
                    <p className="text-xs text-muted-foreground">Gauge interest before events</p>
                  </div>
                </div>
              </Link>

              <Link to="/find-expats" onClick={() => setMobileMenuOpen(false)}>
                <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-green-500/10">
                  <div className="w-10 h-10 bg-green-500/10 rounded-lg flex items-center justify-center">
                    <Globe className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-bold">Find Expats</p>
                    <p className="text-xs text-muted-foreground">Connect with others</p>
                  </div>
                </div>
              </Link>

              {user && (
                <Link to="/connections" onClick={() => setMobileMenuOpen(false)} data-testid="mobile-nav-my-connections-link">
                  <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-pink-500/10">
                    <div className="w-10 h-10 bg-pink-500/10 rounded-lg flex items-center justify-center relative">
                      <Users className="w-5 h-5 text-pink-600" />
                      {pendingConnections > 0 && (
                        <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
                          {pendingConnections > 9 ? '9+' : pendingConnections}
                        </span>
                      )}
                    </div>
                    <div>
                      <p className="font-bold">My Connections</p>
                      <p className="text-xs text-muted-foreground">
                        {pendingConnections > 0
                          ? `${pendingConnections} pending request${pendingConnections === 1 ? '' : 's'}`
                          : 'People you\'re connected with'}
                      </p>
                    </div>
                  </div>
                </Link>
              )}

              {/* Services Section */}
              <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider px-2 pt-4">Services</p>

              <Link to="/jobs" onClick={() => setMobileMenuOpen(false)}>
                <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-accent/10">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <p className="font-bold">Jobs</p>
                    <p className="text-xs text-muted-foreground">Find opportunities</p>
                  </div>
                </div>
              </Link>

              <Link to="/housing" onClick={() => setMobileMenuOpen(false)}>
                <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-primary/10">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Home className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-bold">Housing</p>
                    <p className="text-xs text-muted-foreground">Rentals & properties</p>
                  </div>
                </div>
              </Link>

              <Link to="/marketplace" onClick={() => setMobileMenuOpen(false)}>
                <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-pink-500/10">
                  <div className="w-10 h-10 bg-pink-500/10 rounded-lg flex items-center justify-center">
                    <ShoppingBag className="w-5 h-5 text-pink-600" />
                  </div>
                  <div>
                    <p className="font-bold">Marketplace</p>
                    <p className="text-xs text-muted-foreground">Buy & sell items</p>
                  </div>
                </div>
              </Link>

              <Link to="/businesses" onClick={() => setMobileMenuOpen(false)}>
                <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-accent/10">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Building className="w-5 h-5 text-accent" />
                  </div>
                  <div>
                    <p className="font-bold">Businesses</p>
                    <p className="text-xs text-muted-foreground">Local services</p>
                  </div>
                </div>
              </Link>

              <Link to="/guides" onClick={() => setMobileMenuOpen(false)}>
                <div className="flex items-center gap-3 p-3 rounded-xl hover:bg-primary/10">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-bold">Resources</p>
                    <p className="text-xs text-muted-foreground">Tips & articles</p>
                  </div>
                </div>
              </Link>

              {/* Actions Section */}
              <div className="border-t border-border/40 pt-4 mt-2 space-y-2">
                {/* Community Selector for Mobile */}
                {user && (
                  <div className="mb-4">
                    <CommunitySelector user={user} variant="settings" />
                  </div>
                )}
                
                {/* Always show Download App button */}
                <Button 
                  onClick={() => { handleInstallClick(); setMobileMenuOpen(false); }}
                  variant="outline"
                  className="w-full font-bold h-12 rounded-full border-2" 
                  data-testid="mobile-install-app"
                >
                  <Download className="w-4 h-4 mr-2" />
                  {isStandalone ? 'App Installed ✓' : 'Download App'}
                </Button>

                {user ? (
                  <>
                    <Link to="/dashboard" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full h-12 rounded-full font-bold relative">
                        <Calendar className="w-4 h-4 mr-2" />
                        My Events
                        {newRsvps > 0 && (
                          <span className="ml-auto bg-green-500 text-white text-xs px-2 py-0.5 rounded-full">
                            {newRsvps} RSVP{newRsvps > 1 ? 's' : ''}
                          </span>
                        )}
                      </Button>
                    </Link>
                    <Link to="/saved" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full h-12 rounded-full font-bold">
                        <Heart className="w-4 h-4 mr-2" />
                        Saved Items
                      </Button>
                    </Link>
                    <Link to="/profile" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full h-12 rounded-full font-bold relative">
                        <UserCircle className="w-4 h-4 mr-2" />
                        Profile
                        {pendingConnections > 0 && (
                          <span className="ml-auto bg-primary text-white text-xs px-2 py-0.5 rounded-full">
                            {pendingConnections} request{pendingConnections > 1 ? 's' : ''}
                          </span>
                        )}
                      </Button>
                    </Link>
                    <Link to="/privacy-settings" onClick={() => setMobileMenuOpen(false)}>
                      <Button variant="outline" className="w-full h-12 rounded-full font-bold">
                        <Settings className="w-4 h-4 mr-2" />
                        Privacy Settings
                      </Button>
                    </Link>
                    <Button
                      onClick={() => { localStorage.removeItem('tutorialCompleted'); setShowTutorial(true); setMobileMenuOpen(false); }}
                      variant="outline"
                      className="w-full h-12 rounded-full font-bold"
                    >
                      <HelpCircle className="w-4 h-4 mr-2" />
                      Take a Tour
                    </Button>
                    <Button
                      onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                      variant="outline"
                      className="w-full h-12 rounded-full font-bold text-red-600 border-red-200 hover:bg-red-50"
                    >
                      Logout
                    </Button>
                  </>
                ) : (
                  <Button
                    onClick={() => { setAuthMessage(''); setShowAuth(true); setMobileMenuOpen(false); }}
                    className="w-full bg-accent hover:bg-accent/90 text-black h-12 rounded-full font-bold"
                  >
                    Sign In
                  </Button>
                )}
              </div>
            </div>
          </div>
        </div>      )}

      {showAuth && <AuthDialog open={showAuth} onClose={() => setShowAuth(false)} customMessage={authMessage} defaultToSignup={authMessage !== ''} />}
      
      <TutorialTour 
        open={showTutorial} 
        onClose={() => setShowTutorial(false)}
        onComplete={() => setShowTutorial(false)}
      />
    </>
  );
};

export default Navbar;